'use client';
import { twMerge } from 'tailwind-merge';
import clsx from 'clsx';
import { ImageMissing } from '../../components';
import ChevronLeft from '../icons/ChevronLeft';
import ChevronRight from '../icons/ChevronRight';
import { useState, useRef, TouchEvent } from 'react';

type ProductListGridItemImageProps = {
  images?: { url: string; type: string }[];
  image?: React.ReactNode;
  size?: 'sm' | 'md' | 'lg';
  topOffset?: number;
};

export const ProductListGridItemImage = ({
  images,
  image,
  size,
  topOffset = 0,
}: ProductListGridItemImageProps) => {
  const [currentGalleryImage, setCurrentGalleryImage] = useState(0);
  const hasGallery = images && images.length > 1;

  // Add touch handling state
  const touchStartX = useRef<number>(0);
  const touchEndX = useRef<number>(0);

  const handleTouchStart = (e: TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e: TouchEvent) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    const swipeDistance = touchEndX.current - touchStartX.current;
    const minSwipeDistance = 50; // Minimum distance for swipe to register

    if (Math.abs(swipeDistance) > minSwipeDistance && hasGallery) {
      if (swipeDistance > 0) {
        // Swipe right - show previous image
        setCurrentGalleryImage((state) =>
          state - 1 < 0 ? images!.length - 1 : state - 1
        );
      } else {
        // Swipe left - show next image
        setCurrentGalleryImage((state) =>
          state + 1 >= images!.length ? 0 : state + 1
        );
      }
    }
  };

  return (
    <>
      <div
        className="mix-blend-multiply overflow-hidden absolute top-0 left-0 size-full"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {hasGallery ? (
          <div className="relative w-full h-full">
            <div
              className="absolute top-0 left-0 w-full h-full transition-transform duration-500 flex"
              style={{
                transform: `translateX(-${currentGalleryImage * 100}%)`,
              }}
            >
              {images.map((img, index) => (
                <img
                  key={index}
                  src={img.url}
                  alt={''}
                  style={
                    img.type === 'single'
                      ? { paddingTop: `${topOffset}px` }
                      : {}
                  }
                  className={twMerge(
                    clsx('w-full h-full min-w-full object-cover', {
                      'pb-8 px-8 object-contain': img.type === 'single',
                    })
                  )}
                  draggable={false}
                />
              ))}
            </div>
          </div>
        ) : image ? (
          <div
            className="w-full h-full pb-8 px-8"
            style={{ paddingTop: `${topOffset}px` }}
          >
            <div className="w-full h-full relative">{image}</div>
          </div>
        ) : (
          <ImageMissing size={size || 'md'} />
        )}
      </div>
      {hasGallery && (
        <div className="flex gap-2 justify-self-center absolute bottom-2 left-0 w-full justify-center">
          {images.map((img, index) => (
            <div
              key={index}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setCurrentGalleryImage(index);
              }}
              className={twMerge(
                clsx(
                  'size-2 rounded-full bg-neutral-50 hover:bg-neutral-70 cursor-pointer',
                  {
                    'bg-neutral-70': index === currentGalleryImage,
                  }
                )
              )}
            ></div>
          ))}
        </div>
      )}
      {hasGallery && (
        <div
          className="w-6 absolute h-full top-0 left-0 cursor-pointer hidden lg:group-hover:flex justify-center items-center"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setCurrentGalleryImage((state) => {
              return state - 1 < 0 ? images.length - 1 : state - 1;
            });
          }}
        >
          <ChevronLeft className="w-6 h-6 text-neutral-0" />
        </div>
      )}
      {hasGallery && (
        <div
          className="w-6 absolute h-full top-0 right-0 cursor-pointer hidden lg:group-hover:flex justify-center items-center"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setCurrentGalleryImage((state) => {
              return state + 1 >= images.length ? 0 : state + 1;
            });
          }}
        >
          <ChevronRight className="w-6 h-6 text-neutral-0" />
        </div>
      )}
    </>
  );
};
